import { CSSProperties } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useMaestroBrief } from '../../../lib/use-maestro-brief';
import { useMyAgents } from '../../../lib/my-agents';
import { Button } from '../ui';
import { AgentDot } from '../../AgentDot';
import { BriefMark } from './BriefMark';
import { BriefArrowIcon } from './BriefArrowIcon';
import { BriefListenButton } from './BriefListenButton';
import { BRIEF_META_TEXT } from './styles';

// The Home 2 morning-brief hero — the redesigned "active brief" state
// (Figma 1859:2668). It replaces the wrap-up headline with ONE agent's actual line from the meeting,
// big and in quotation marks, attributed to that agent. Everything else about the brief state logic is
// unchanged (BriefSlot still resolves `hero` vs the other states); only this finished-brief presentation
// differs from `MaestroBrief`.
//
// The card is our white DS `Card` (Figma "Symphony Card Box"), supplied by BriefSlot — this renders the
// card CONTENT only.
//
// The card's five elements (product notes on the Figma node):
//   1. Title      — "Your agents' daily meeting, {time}" beside the bento meeting mark.
//   2. Attendees  — the overlapping agent dots + "N Agents met".
//   3. The quote  — one agent's line, big, in quotation marks. The heart of the card.
//   4. Attribution— that agent's "Name · Role", flowing right after the quote.
//   5. Actions    — "Read the full agents meeting" (opens it) + a Listen icon button.

const TITLE_TEXT: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 24,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '28px',
  letterSpacing: '-0.48px',
  color: 'rgb(var(--ink-rgb))',
  fontFeatureSettings: "'dlig' on",
};

// The big quoted line — Archivo (the brief display face) at the design's 32/40, -0.64 (Figma 1859:2699).
const QUOTE_TEXT: CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontSize: 32,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '40px',
  letterSpacing: '-0.64px',
  color: 'rgb(var(--ink-rgb))',
  fontFeatureSettings: "'dlig' on",
  margin: 0,
};

// The attribution flows inline at the END of the quote (Figma places it on the quote's last line), so it
// is an inline-flex appended to the quote paragraph, vertically centered within the 40px quote line box.
const ATTRIBUTION: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
  marginInlineStart: 8,
};

export function Home2Brief() {
  const { t } = useTranslation();
  const brief = useMaestroBrief();
  const agentsState = useMyAgents();

  if (!brief) return null;
  const { agentIds, agentCount, time, quote } = brief;

  const quoteAgent =
    quote && agentsState.status === 'ready'
      ? agentsState.team.agents.find((a) => a.id === quote.agentId)
      : undefined;

  // The big line: the featured agent quote when we have one, else the wrap-up headline (a convening
  // brief has no agent turns yet). Attribution only rides along with a real quote.
  const bigText = quote?.text ?? brief.headline;

  return (
    <div style={{ textAlign: 'start' }}>
      <div
        className="brief-reveal"
        style={{ display: 'flex', alignItems: 'center', gap: 20, rowGap: 10, flexWrap: 'wrap', animationDelay: '0s' }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <BriefMark size={20} />
          <span style={{ ...TITLE_TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t('brief.home2.title', { time })}
          </span>
        </span>
        {agentCount > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {agentIds.slice(0, 4).map((id, i) => (
                <span key={id} style={{ marginInlineStart: i === 0 ? 0 : -8, display: 'inline-flex' }}>
                  <AgentDot id={id} size={20} />
                </span>
              ))}
            </span>
            <span style={BRIEF_META_TEXT}>
              {t(agentCount === 1 ? 'brief.home2.agentMet' : 'brief.home2.agentsMet', { count: agentCount })}
            </span>
          </span>
        )}
      </div>

      {bigText && (
        <p className="brief-reveal" style={{ ...QUOTE_TEXT, marginTop: 20, animationDelay: '0.08s' }}>
          {quote ? `“${bigText}”` : bigText}
          {quote && quoteAgent && (
            <span style={ATTRIBUTION}>
              <AgentDot id={quote.agentId} size={20} />
              <span style={BRIEF_META_TEXT}>
                {quoteAgent.title ? `${quoteAgent.name} · ${quoteAgent.title}` : quoteAgent.name}
              </span>
            </span>
          )}
        </p>
      )}

      {/* Read + Listen share a center-aligned row so the icon button lines up with the primary button
          (matching MaestroBrief) rather than sitting on its text baseline. */}
      <div className="brief-reveal" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20, animationDelay: '0.16s' }}>
        <Button data-card-primary size="large" onClick={brief.open} endIcon={<BriefArrowIcon />}>
          {t('brief.home2.readFullMeeting')}
        </Button>
        <BriefListenButton iconOnly size="large" />
      </div>
    </div>
  );
}
